import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../../../../components/navbar';
import Footer from '../../../../components/footer';
import Button from '../../../../components/Button';
import Checkbox from '../../../../components/Checkbox';
import Dropdown from '../../../../components/Dropdown';
import { useConsultation } from '../../../../context/ConsultationContext';
import { handleTreatmentServiceNavigation } from '../components/treatmentNavigationHelper';
import baseStyles from './css/BloodPressure.module.css';
import styles from './css/MetabolicConsult.module.css';
import { PersonStanding, Send } from 'lucide-react';

const MetabolicConsult = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { consultationData, updateConsultationData, getStepDraft, setStepDraft, goToStep } = useConsultation();

    const patient = location?.state?.patient || consultationData?.patient || {};
    const patientName = `${patient?.firstname || ""} ${patient?.surname || ""}`.trim() || "Thandiwe Ndlovu";
    const profileNumber = patient?.profilenumber || "448896";
    const initials = `${patient?.firstname?.[0] || ""}${patient?.surname?.[0] || ""}`.toUpperCase() || "TN";

    // Symptom checklist states
    const [symptoms, setSymptoms] = useState({
        hotFlashes: false,
        weightGain: false,
        sleepDisturbances: false,
        nightSweats: false,
        fatigue: false,
        moodChanges: false,
        jointPain: false,
        brainFog: false
    });
    
    // History and medication states
    const [hormonalHistory, setHormonalHistory] = useState('');
    const [currentMedications, setCurrentMedications] = useState('');
    
    // Lifestyle factors states
    const [exercise, setExercise] = useState('');
    const [stressLevel, setStressLevel] = useState('');
    const [sleepQuality, setSleepQuality] = useState('');
    const [dietQuality, setDietQuality] = useState('');
    
    // Other states
    const [clinicalNotes, setClinicalNotes] = useState('');

    const handleSymptomChange = (key) => {
        setSymptoms(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleInvoice = () => {
        handleTreatmentServiceNavigation({
            location,
            navigate,
            consultationData,
            updateConsultationData,
            getStepDraft,
            setStepDraft,
            goToStep,
            serviceData: {
                metabolic_consult: {
                    symptoms,
                    hormonalHistory,
                    currentMedications,
                    exercise,
                    stressLevel,
                    sleepQuality,
                    dietQuality,
                    clinicalNotes,
                },
            },
        });
    };

    useEffect(() => {
        document.body.classList.add(baseStyles.hideSidebar);
        return () => {
            document.body.classList.remove(baseStyles.hideSidebar);
        };
    }, []);

    return (
        <div className={baseStyles.bloodPressureContainer}>
            <Navbar />

            <main className={baseStyles.mainContent}>
                <div className={baseStyles.card}>
                    <div className={baseStyles.cardHeader}>
                        <div className={baseStyles.headerIcon} aria-hidden="true">
                            <PersonStanding />
                        </div>
                        <h1 className={styles.title}>Metabolic / Menopause Consult (45 min)</h1>
                    </div>

                    <div className={baseStyles.patientCard}>
                        <div className={baseStyles.avatar} aria-hidden="true">{initials}</div>
                        <div className={baseStyles.patientText}>
                            <div className={baseStyles.patientName}>{patientName}</div>
                            <div className={baseStyles.patientMetaRow}>
                                <span className={baseStyles.profileInfo}>Profile: {profileNumber}</span>
                                <span className={baseStyles.dependentBadge}>Dependent</span>
                            </div>
                        </div>
                    </div>

                    <div className={baseStyles.sectionBlock}>
                        <div className={baseStyles.sectionTitle}>SYMPTOM CHECKLIST</div>
                        <div className={styles.symptomsGrid}>
                            <div className={styles.symptomColumn}>
                                <div className={baseStyles.checkboxItem}>
                                    <Checkbox
                                        id="hotFlashes"
                                        checked={symptoms.hotFlashes}
                                        onChange={() => handleSymptomChange('hotFlashes')}
                                    />
                                    <label htmlFor="hotFlashes" className={baseStyles.checkboxLabel}>Hot Flashes</label>
                                </div>

                                <div className={baseStyles.checkboxItem}>
                                    <Checkbox
                                        id="weightGain"
                                        checked={symptoms.weightGain}
                                        onChange={() => handleSymptomChange('weightGain')}
                                    />
                                    <label htmlFor="weightGain" className={baseStyles.checkboxLabel}>Weight Gain</label>
                                </div>

                                <div className={baseStyles.checkboxItem}>
                                    <Checkbox
                                        id="sleepDisturbances"
                                        checked={symptoms.sleepDisturbances}
                                        onChange={() => handleSymptomChange('sleepDisturbances')}
                                    />
                                    <label htmlFor="sleepDisturbances" className={baseStyles.checkboxLabel}>Sleep disturbances</label>
                                </div>

                                <div className={baseStyles.checkboxItem}>
                                    <Checkbox
                                        id="nightSweats"
                                        checked={symptoms.nightSweats}
                                        onChange={() => handleSymptomChange('nightSweats')}
                                    />
                                    <label htmlFor="nightSweats" className={baseStyles.checkboxLabel}>Night sweats</label>
                                </div>
                            </div>

                            <div className={styles.symptomColumn}>
                                <div className={baseStyles.checkboxItem}>
                                    <Checkbox
                                        id="fatigue"
                                        checked={symptoms.fatigue}
                                        onChange={() => handleSymptomChange('fatigue')}
                                    />
                                    <label htmlFor="fatigue" className={baseStyles.checkboxLabel}>Fatigue</label>
                                </div>

                                <div className={baseStyles.checkboxItem}>
                                    <Checkbox
                                        id="moodChanges"
                                        checked={symptoms.moodChanges}
                                        onChange={() => handleSymptomChange('moodChanges')}
                                    />
                                    <label htmlFor="moodChanges" className={baseStyles.checkboxLabel}>Mood changes</label>
                                </div>

                                <div className={baseStyles.checkboxItem}>
                                    <Checkbox
                                        id="jointPain"
                                        checked={symptoms.jointPain}
                                        onChange={() => handleSymptomChange('jointPain')}
                                    />
                                    <label htmlFor="jointPain" className={baseStyles.checkboxLabel}>Joint pain</label>
                                </div>

                                <div className={baseStyles.checkboxItem}>
                                    <Checkbox
                                        id="brainFog"
                                        checked={symptoms.brainFog}
                                        onChange={() => handleSymptomChange('brainFog')}
                                    />
                                    <label htmlFor="brainFog" className={baseStyles.checkboxLabel}>Brain fog</label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={baseStyles.sectionBlock}>
                        <div className={baseStyles.sectionTitle}>HISTORY AND MEDICATION</div>
                        <div className={styles.readingsRow}>
                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Hormonal History</label>
                                <div className={baseStyles.inputGroup}>
                                    <input
                                        type="text"
                                        value={hormonalHistory}
                                        onChange={(e) => setHormonalHistory(e.target.value)}
                                        className={baseStyles.readingInput}
                                        placeholder='e.g., Perimenopausal 2 years'
                                    />
                                </div>
                            </div>

                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Current Medications</label>
                                <div className={baseStyles.inputGroup}>
                                    <input
                                        type="text"
                                        value={currentMedications}
                                        onChange={(e) => setCurrentMedications(e.target.value)}
                                        className={baseStyles.readingInput}
                                        placeholder='e.g., HRT, Supplements'
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={baseStyles.sectionBlock}>
                        <div className={baseStyles.sectionTitle}>LIFESTYLE FACTORS</div>
                        <div className={styles.readingsRow1}>
                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Exercise</label>
                                <div className={baseStyles.inputGroup}>
                                    <Dropdown
                                        name="exercise"
                                        value={exercise}
                                        onChange={setExercise}
                                        options={['None', '1-2 times/week', '3-4 times/week', '5+ times/week']}
                                        className={styles.readingInput}
                                        alignLeft
                                    />
                                </div>
                            </div>

                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Stress Level</label>
                                <div className={baseStyles.inputGroup}>
                                    <Dropdown
                                        name="stressLevel"
                                        value={stressLevel}
                                        onChange={setStressLevel}
                                        options={['Low', 'Moderate', 'High']}
                                        className={styles.readingInput}
                                        alignLeft
                                    />
                                </div>
                            </div>

                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Sleep Quality</label>
                                <div className={baseStyles.inputGroup}>
                                    <Dropdown
                                        name="sleepQuality"
                                        value={sleepQuality}
                                        onChange={setSleepQuality}
                                        options={['Poor', 'Fair', 'Good', 'Excellent']}
                                        className={styles.readingInput}
                                        alignLeft
                                    />
                                </div>
                            </div>

                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Diet Quality</label>
                                <div className={baseStyles.inputGroup}>
                                    <Dropdown
                                        name="dietQuality"
                                        value={dietQuality}
                                        onChange={setDietQuality}
                                        options={['Poor', 'Fair', 'Good', 'Excellent']}
                                        className={styles.readingInput}
                                        alignLeft
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={baseStyles.sectionBlock}>
                        <div className={baseStyles.fieldLabel}>Treatment Plan Notes</div>
                        <textarea
                            value={clinicalNotes}
                            onChange={(e) => setClinicalNotes(e.target.value)}
                            className={baseStyles.clinicalNotes}
                            placeholder="Enter clinical notes..."
                            rows={6}
                        />
                    </div>

                    <div className={baseStyles.cardFooter}>
                        <Button
                            onClick={handleInvoice}
                            className={baseStyles.invoiceButton}
                            type="button"
                        >
                            <Send size={16} />
                            Invoice
                        </Button>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default MetabolicConsult;